import FalconCardHeader from 'components/common/FalconCardHeader';
import Flex from 'components/common/Flex';
import BasicECharts from 'components/common/BasicEChart';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import { PieChart } from 'echarts/charts';
import {
  GridComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import { useAppContext } from 'providers/AppProvider';
import React, { useMemo, useState } from 'react';
import { Card, Col, Row, Modal, Button, Badge, Form, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer
]);

const LATE_FIELDS = [
  { key: 'billed_late', name: 'Billed', color: '#ff8566' },
  { key: 'robot_late', name: 'Robot', color: '#1000ff' },
  { key: 'manual_late', name: 'Manual', color: '#8b0a8f' },
  { key: 'pv2_late', name: 'PV2', color: '#008a0a' },
  { key: 'manifest_late', name: 'Manifest', color: '#9a4f12' }
];

const SORT_OPTIONS = [
  { value: 'DAYS_LATE_DESC', label: 'Days Late (Highest First)' },
  { value: 'DELIVERY_DATE_DESC', label: 'Delivery Date (Newest First)' },
  // { value: 'LATE_COUNT_DESC', label: 'Late Cycles (Highest First)' },
  { value: 'NAME_ASC', label: 'Facility Name (A-Z)' }
];

const URGENCY_OPTIONS = [
  { value: 'ALL', label: 'All Urgencies' },
  { value: 'CRITICAL', label: 'Critical (5+ Days)' },
  { value: 'MODERATE', label: 'Moderate (2-4 Days)' },
  { value: 'LOW', label: 'Minor (1 Day)' }
];

const toNumber = value => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const getTimeValue = dateStr => {
  if (!dateStr) return 0;
  const time = new Date(dateStr).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const getFacilityName = facilityId => {
  try {
    const facilitiesData = JSON.parse(
      localStorage.getItem('facilitiesData') || '[]'
    );
    return facilitiesData.find(
      facility => Number(facility.value) === Number(facilityId)
    )?.key;
  } catch {
    return '';
  }
};

const getOptions = (getThemeColor, chartData, totalLate, hasLateData) => ({
  color: chartData.map(item => item.chartColor),
  tooltip: {
    show: hasLateData,
    trigger: 'item',
    padding: [8, 12],
    transitionDuration: 0,
    backgroundColor: getThemeColor('gray-100'),
    borderColor: getThemeColor('gray-300'),
    borderWidth: 1,
    textStyle: { color: getThemeColor('gray-1100') },
    formatter: params =>
      `<strong>${params.name}</strong><br/>${params.value} cycles (${params.percent}%)`
  },
  series: [
    {
      name: 'Late Cycles',
      type: 'pie',
      radius: ['42%', '66%'],
      center: ['50%', '50%'],
      avoidLabelOverlap: true,
      minAngle: 4,
      padAngle: 2,
      label: {
        show: true,
        formatter: '{b}',
        color: getThemeColor('gray-800'),
        fontSize: 11
      },
      labelLine: {
        show: true,
        length: 6,
        length2: 6
      },
      itemStyle: {
        borderRadius: 8,
        borderWidth: 3,
        borderColor: getThemeColor('gray-100')
      },
      emphasis: {
        scale: true,
        scaleSize: 8,
        itemStyle: {
          shadowBlur: 18,
          shadowColor: 'rgba(0, 0, 0, 0.18)'
        }
      },
      data: chartData
    }
  ]
});

const LateMetric = ({ item, total, onClick }) => {
  const percentage =
    total > 0 ? ((item.value * 100) / total).toFixed(1) : '0.0';

  return (
    <div
      className="py-2 px-3 rounded-2 bg-100 transition-base position-relative overflow-hidden cursor-pointer"
      style={{
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out'
      }}
      onClick={() => onClick(item)}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') onClick(item);
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateX(4px)';
        e.currentTarget.style.backgroundColor = 'var(--falcon-gray-200, #edf2f9)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.backgroundColor = '';
      }}
    >
      <Flex alignItems="center" justifyContent="between" className="gap-2">
        <div className="min-w-0">
          <div className="fw-semibold text-900 text-truncate">
            <FontAwesomeIcon
              icon="circle"
              className="me-2"
              style={{ color: item.color }}
            />
            {item.name}
          </div>
          <div className="fs-11 text-600">{percentage}% of late cycles</div>
        </div>
        <div className="fw-bold d-flex align-items-center gap-2" style={{ color: item.color }}>
          {item.value}
          <FontAwesomeIcon icon="chevron-right" className="fs-11 text-400" />
        </div>
      </Flex>
      <div className="w-100 bg-200 rounded-pill overflow-hidden mt-1.5" style={{ height: '3px' }}>
        <div
          style={{
            width: `${percentage}%`,
            backgroundColor: item.color,
            height: '100%',
            transition: 'width 0.4s ease'
          }}
        />
      </div>
    </div>
  );
};

LateMetric.propTypes = {
  item: PropTypes.shape({
    key: PropTypes.string,
    color: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired
  }).isRequired,
  total: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired
};

const CycleSummary = ({ cycleSummaryDetails = {} }) => {
  const { getThemeColor } = useAppContext();
  const navigate = useNavigate();
  const [selectedFacility, setSelectedFacility] = useState({
    value: 'ALL',
    label: 'All Facilities'
  });

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedModalFacility, setSelectedModalFacility] = useState(null);
  const [modalSearchText, setModalSearchText] = useState('');
  const [selectedSort, setSelectedSort] = useState(SORT_OPTIONS[0]);
  const [selectedUrgency, setSelectedUrgency] = useState(URGENCY_OPTIONS[0]);

  const facilityRows = Array.isArray(
    cycleSummaryDetails?.cycleSummaryByFacility
  )
    ? cycleSummaryDetails.cycleSummaryByFacility
    : [];

  const facilityOptions = useMemo(
    () => [
      { value: 'ALL', label: 'All Facilities' },
      ...facilityRows.map(facility => ({
        value: String(facility.facility_id),
        label:
          getFacilityName(facility.facility_id) ||
          `Facility ${facility.facility_id}`
      }))
    ],
    [facilityRows]
  );

  const selectedSummary = useMemo(() => {
    if (selectedFacility && selectedFacility.value !== 'ALL') {
      return (
        facilityRows.find(
          facility =>
            String(facility.facility_id) === String(selectedFacility.value)
        ) || {}
      );
    }

    if (cycleSummaryDetails?.cycleSummary) {
      return cycleSummaryDetails.cycleSummary;
    }

    return facilityRows.reduce((acc, facility) => {
      LATE_FIELDS.forEach(({ key }) => {
        acc[key] = toNumber(acc[key]) + toNumber(facility[key]);
      });
      acc.total_cycles =
        toNumber(acc.total_cycles) + toNumber(facility.total_cycles);
      return acc;
    }, {});
  }, [cycleSummaryDetails, facilityRows, selectedFacility]);

  const lateItems = useMemo(
    () =>
      LATE_FIELDS.map(field => ({
        ...field,
        value: toNumber(selectedSummary[field.key]),
        chartColor: field.color
      })),
    [selectedSummary]
  );

  const totalLate = lateItems.reduce((total, item) => total + item.value, 0);
  const hasLateData = totalLate > 0;
  const chartData = hasLateData
    ? lateItems
    : [
        {
          name: 'No Late Cycles',
          value: 1,
          color: 'secondary',
          chartColor: getThemeColor('gray-300')
        }
      ];

  const handleOpenModal = category => {
    if (!category || !category.key) return;
    setSelectedCategory(category);
    setSelectedModalFacility(null);
    setModalSearchText('');
    setSelectedSort(SORT_OPTIONS[0]);
    setSelectedUrgency(URGENCY_OPTIONS[0]);
    setShowModal(true);
  };

  const handleChartClick = params => {
    if (params && params.data && params.data.key) {
      handleOpenModal(params.data);
    }
  };

  const rawMatchingFacilities = useMemo(() => {
    if (!selectedCategory || !selectedCategory.key) return [];

    return facilityRows
      .filter(facility => toNumber(facility[selectedCategory.key]) > 0)
      .map(facility => {
        const lateCount = toNumber(facility[selectedCategory.key]);
        const name =
          getFacilityName(facility.facility_id) ||
          `Facility ${facility.facility_id}`;

        let daysLate = 0;
        if (
          facility.days_until_delivery !== undefined &&
          facility.days_until_delivery !== null
        ) {
          daysLate = Math.abs(toNumber(facility.days_until_delivery));
        } else if (selectedCategory?.key) {
          const catPrefix = selectedCategory.key.replace('_late', '');
          const catDaysKey = `${catPrefix}_days_late`;
          if (
            facility[catDaysKey] !== undefined &&
            facility[catDaysKey] !== null
          ) {
            daysLate = Math.abs(toNumber(facility[catDaysKey]));
          }
        }

        return {
          ...facility,
          facilityName: name,
          lateCount,
          daysLate
        };
      });
  }, [facilityRows, selectedCategory]);

  const filteredFacilities = useMemo(() => {
    let list = [...rawMatchingFacilities];

    if (modalSearchText.trim()) {
      const q = modalSearchText.toLowerCase();
      list = list.filter(f => f.facilityName.toLowerCase().includes(q));
    }

    if (selectedUrgency.value === 'CRITICAL') {
      list = list.filter(f => f.daysLate >= 5);
    } else if (selectedUrgency.value === 'MODERATE') {
      list = list.filter(f => f.daysLate >= 2 && f.daysLate <= 4);
    } else if (selectedUrgency.value === 'LOW') {
      list = list.filter(f => f.daysLate < 2);
    }

    return list.sort((a, b) => {
      switch (selectedSort.value) {
        case 'DELIVERY_DATE_DESC': {
          const timeA = getTimeValue(a.effective_delivery_date);
          const timeB = getTimeValue(b.effective_delivery_date);
          if (timeB !== timeA) return timeB - timeA;
          return b.daysLate - a.daysLate;
        }
        case 'LATE_COUNT_DESC': {
          if (b.lateCount !== a.lateCount) return b.lateCount - a.lateCount;
          return b.daysLate - a.daysLate;
        }
        case 'NAME_ASC': {
          return a.facilityName.localeCompare(b.facilityName);
        }
        case 'DAYS_LATE_DESC':
        default: {
          if (b.daysLate !== a.daysLate) return b.daysLate - a.daysLate;
          const timeA = getTimeValue(a.effective_delivery_date);
          const timeB = getTimeValue(b.effective_delivery_date);
          if (timeB !== timeA) return timeB - timeA;
          return b.lateCount - a.lateCount;
        }
      }
    });
  }, [rawMatchingFacilities, modalSearchText, selectedUrgency, selectedSort]);

  const modalFacilityOptions = useMemo(() => {
    if (!rawMatchingFacilities) return [];
    return rawMatchingFacilities.map(f => ({
      value: String(f.facility_id),
      label: `${f.facilityName}${
        f.daysLate ? ` (${f.daysLate} ${f.daysLate === 1 ? 'day' : 'days'} late)` : ''
      }`
    }));
  }, [rawMatchingFacilities]);

  const maxDaysLate = useMemo(() => {
    if (rawMatchingFacilities.length === 0) return 0;
    return Math.max(...rawMatchingFacilities.map(f => f.daysLate || 0));
  }, [rawMatchingFacilities]);

  const avgDaysLate = useMemo(() => {
    if (rawMatchingFacilities.length === 0) return 0;
    const sum = rawMatchingFacilities.reduce((acc, f) => acc + (f.daysLate || 0), 0);
    return (sum / rawMatchingFacilities.length).toFixed(1);
  }, [rawMatchingFacilities]);

  const handleFacilityClick = facilityId => {
    setShowModal(false);
    navigate(`/main/peltask/cycledetails/${facilityId}`);
  };

  const getLateByColor = days => {
    if (days >= 5) return '#e63757';
    if (days >= 2) return '#d97706';
    return '#008a0a';
  };

  return (
    <>
      <Card className="h-100 overflow-hidden shadow-sm">
        <FalconCardHeader
          title="Cycle Late Summary"
          light={false}
          titleTag="h6"
          className="pb-0 d-flex align-items-center justify-content-between flex-wrap gap-2"
          endEl={
            <div style={{ minWidth: '180px' }}>
              <ThemedSelect
                options={facilityOptions}
                value={selectedFacility}
                onChange={option => setSelectedFacility(option || facilityOptions[0])}
                placeholder="Filter by facility"
                isSearchable
                size="sm"
              />
            </div>
          }
        />
        <Card.Body className="py-2.5 px-3">
          <Row className="g-3 gx-lg-4 align-items-center">
            <Col xs={12} lg={6}>
              <div className="d-flex flex-column gap-2">
                {lateItems.map(item => (
                  <LateMetric
                    key={item.key}
                    item={item}
                    total={totalLate}
                    onClick={handleOpenModal}
                  />
                ))}
              </div>
            </Col>

            <Col xs={12} lg={6}>
              <div className="d-flex align-items-center justify-content-center position-relative">
                <BasicECharts
                  echarts={echarts}
                  options={getOptions(
                    getThemeColor,
                    chartData,
                    totalLate,
                    hasLateData
                  )}
                  onEvents={{
                    click: handleChartClick
                  }}
                  style={{ width: '100%', minHeight: '15.5rem' }}
                />
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="bg-100 py-2.5">
          <Modal.Title as="h5" className="d-flex align-items-center gap-2 mb-0">
            <FontAwesomeIcon
              icon="circle"
              style={{ color: selectedCategory?.color }}
            />
            <span>{selectedCategory?.name} Late Facilities</span>
            <Badge
              pill
              className="ms-2 fs-11"
              style={{
                backgroundColor: selectedCategory?.color,
                color: '#fff'
              }}
            >
              {rawMatchingFacilities.length} {rawMatchingFacilities.length === 1 ? 'Facility' : 'Facilities'}
            </Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <Row className="g-2 mb-3">
            <Col xs={12}>
              <InputGroup size="sm">
                <InputGroup.Text className="bg-white border-end-0 text-400">
                  <FontAwesomeIcon icon="search" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Filter facility list..."
                  className="border-start-0 ps-0"
                  value={modalSearchText}
                  onChange={e => setModalSearchText(e.target.value)}
                />
              </InputGroup>
            </Col>
            {/* <Col xs={12} md={6}>
              <ThemedSelect
                options={modalFacilityOptions}
                value={selectedModalFacility}
                onChange={option => {
                  setSelectedModalFacility(option);
                  if (option && option.value) {
                    handleFacilityClick(option.value);
                  }
                }}
                placeholder="Search & jump to facility..."
                isSearchable
                isClearable
                menuPortalTarget={document.body}
              />
            </Col> */}
            <Col md={6}>
              <ThemedSelect
                options={URGENCY_OPTIONS}
                value={selectedUrgency}
                onChange={option => setSelectedUrgency(option || URGENCY_OPTIONS[0])}
                placeholder="Urgency..."
                isSearchable={false}
                size="sm"
              />
            </Col>
            <Col md={6}>
              <ThemedSelect
                options={SORT_OPTIONS}
                value={selectedSort}
                onChange={option => setSelectedSort(option || SORT_OPTIONS[0])}
                placeholder="Sort by..."
                isSearchable={false}
                size="sm"
              />
            </Col>
          </Row>

          {filteredFacilities.length === 0 ? (
            <div className="text-center py-5 text-500 fs-10 border rounded-2 bg-100">
              <FontAwesomeIcon icon="hospital" className="fs-4 text-400 mb-2" />
              <div>No matching facilities found.</div>
            </div>
          ) : (
            <div
              className="d-flex flex-column gap-2"
              style={{ maxHeight: '420px', overflowY: 'auto' }}
            >
              {filteredFacilities.map(facility => (
                <div
                  key={facility.facility_id}
                  onClick={() => handleFacilityClick(facility.facility_id)}
                  className="p-3 rounded-2 border d-flex align-items-center justify-content-between transition-base position-relative"
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.15s ease-in-out',
                    backgroundColor:
                      facility.lateCount > 0 ? 'var(--falcon-gray-100, #f9fbfd)' : '#ffffff'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow =
                      '0 0.25rem 0.5rem rgba(0, 0, 0, 0.08)';
                    e.currentTarget.style.borderColor = selectedCategory?.color || '#3874ff';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '';
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center fw-bold shadow-sm"
                      style={{
                        width: '42px',
                        height: '42px',
                        backgroundColor:
                          facility.lateCount > 0
                            ? `${selectedCategory?.color}18`
                            : '#f0f0f0',
                        color:
                          facility.lateCount > 0
                            ? selectedCategory?.color
                            : '#6c757d',
                        fontSize: '15px'
                      }}
                    >
                      <FontAwesomeIcon icon="hospital-user" />
                    </div>
                    <div>
                      <div className="fw-semibold text-900 fs-10">
                        {facility.facilityName}
                      </div>
                      <div className="d-flex align-items-center gap-3 fs-11 text-500 mt-1 flex-wrap">
                        {facility.effective_delivery_date && (
                          <span>
                            Delivery Date:{' '}
                            <strong className="text-700">
                              {moment(facility.effective_delivery_date).isValid()
                                ? moment(facility.effective_delivery_date).format('MMM DD, YYYY')
                                : facility.effective_delivery_date}
                            </strong>
                          </span>
                        )}
                        {facility.cycle_start_date && (
                          <span>
                            Cycle Start:{' '}
                            <strong className="text-700">
                              {moment(facility.cycle_start_date).isValid()
                                ? moment(facility.cycle_start_date).format('MMM DD, YYYY')
                                : facility.cycle_start_date}
                            </strong>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <div className="text-end">
                      <div
                        className="fw-bold"
                        style={{
                          color: getLateByColor(facility.daysLate),
                          fontSize: '14px'
                        }}
                      >
                        Late by {facility.daysLate} {facility.daysLate === 1 ? 'day' : 'days'}
                      </div>
                    </div>
                    <FontAwesomeIcon icon="chevron-right" className="text-400 fs-11 ms-1" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-100 py-2 d-flex justify-content-between align-items-center">
          <div className="fs-11 text-600">
            Showing <strong>{filteredFacilities.length}</strong> of <strong>{rawMatchingFacilities.length}</strong> facilities
            {rawMatchingFacilities.length > 0 && (
              <span> | Max Late: <strong>{maxDaysLate}d</strong> | Avg Late: <strong>{avgDaysLate}d</strong></span>
            )}
          </div>
          <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

CycleSummary.propTypes = {
  cycleSummaryDetails: PropTypes.shape({
    cycleSummary: PropTypes.object,
    cycleSummaryByFacility: PropTypes.arrayOf(PropTypes.object)
  })
};

export default CycleSummary;
